/**
 * Embed pick-bridge helpers — shared by createVideo.jsx (picks/commands) and
 * draggableToolbar.jsx (Black Cherry chat over the bridge).
 *
 * The bridge is active ONLY when the ET runs framed on a public /s/ share page,
 * and ONLY talks to a trusted parent — never an arbitrary one:
 *
 * 1. Web mode: the parent origin must match the explicit allowlist — our own
 *    origin, OUR frontend on Render (prod + its pr-N preview envs), or local
 *    dev. NOT all of *.onrender.com — that's a multi-tenant domain where
 *    anyone can host a page, so a wildcard there would allowlist the whole world.
 *
 * 2. Panel mode (the Premiere Pro CEP panel): CEP panels load from file://,
 *    an origin no WEBSITE can ever have — so a hostile web page can't enter
 *    this mode. The panel passes a session token in the URL hash
 *    (#cc_panel=<token>); every inbound command must echo it as
 *    `ccPanelToken`, so even another local page holding a window reference
 *    can't drive the ET without the token. Outbound posts use targetOrigin
 *    "*" in this mode only (file:// isn't a legal postMessage target), which
 *    is safe because postMessage to window.parent reaches only the parent —
 *    the panel itself.
 */
// cherryclipz.com origins added 2026-09-04 ahead of the domain cutover: the flagship's embedded
// editor refuses any parent not on this list, so without them the live demo and ET->timeline
// would go silent the moment the site is served from the real domain.
const ALLOWED_PARENT = /^(https:\/\/cherryclipz-v2-frontend(-pr-\d+)?\.onrender\.com|https:\/\/(app\.|www\.)?cherryclipz\.com|http:\/\/localhost:\d+)$/;

// ACTIVATION requires the ancestor origin to be EXACTLY "file://" — never "null".
// "null" is what ANY opaque origin serializes to, including a hostile website's
// <iframe sandbox> wrapper, and the hash token is self-declared so it can't help
// there. Only a real file:// parent (the CEP panel — unreachable for websites)
// may enter panel mode. "null" stays accepted ONLY for inbound message origins
// AFTER activation (some CEF builds serialize file-page messages as "null");
// by then ancestorOrigins has proven the parent is file:// and e.source pins
// the sender to that exact window.
const PANEL_ANCESTOR = "file://";
const PANEL_MESSAGE_ORIGINS = ["file://", "null"];
export const PANEL_ORIGIN = "file://"; // sentinel returned by getEmbedParentOrigin in panel mode

/** The CEP panel's session token from the URL hash (#cc_panel=<token>), or "". */
export const getPanelToken = () => {
  if (typeof window === "undefined") return "";
  const m = /(?:^|[#&])cc_panel=([A-Za-z0-9_-]{16,128})(?:&|$)/.exec(window.location.hash || "");
  return m ? m[1] : "";
};

/**
 * Remote-driver (phone → Premiere) session token from the /s/…#cc_remote=<token> link, or "".
 * Present ONLY on a real share page. Unlike the panel bridge this is NOT a postMessage channel —
 * it's the capability the phone uses to POST its pick-set to the backend relay, which the desktop
 * plugin polls. Any device holding an armed token can drive; the owner controls arming + the link.
 */
export const getRemoteDriverToken = () => {
  if (typeof window === "undefined") return "";
  if (!window.location.pathname.startsWith("/s/")) return "";
  const m = /(?:^|[#&])cc_remote=([A-Za-z0-9_-]{16,128})(?:&|$)/.exec(window.location.hash || "");
  return m ? m[1] : "";
};

export const getEmbedParentOrigin = () => {
  if (typeof window === "undefined" || window.self === window.top) return "";
  if (!window.location.pathname.startsWith("/s/")) return "";
  let parentOrigin = "";
  try {
    const ancestors = window.location.ancestorOrigins;
    if (ancestors && ancestors.length) parentOrigin = ancestors[0];
    else if (document.referrer) parentOrigin = new URL(document.referrer).origin;
  } catch (e) { /* ignore */ }
  // Panel mode: EXACTLY a file:// ancestor + a session token in the hash.
  if (parentOrigin === PANEL_ANCESTOR) {
    return getPanelToken() ? PANEL_ORIGIN : "";
  }
  if (!parentOrigin || (parentOrigin !== window.location.origin && !ALLOWED_PARENT.test(parentOrigin))) {
    return "";
  }
  return parentOrigin;
};

/** The targetOrigin to use when posting to the embed parent. */
export const getEmbedPostTarget = () => {
  const origin = getEmbedParentOrigin();
  if (!origin) return "";
  return origin === PANEL_ORIGIN ? "*" : origin;
};

/**
 * True when a message event is a command from the trusted embed parent.
 * Web mode: exact origin match. Panel mode: file:// origin + token echo.
 * Both modes: the sender must BE the parent window.
 */
export const isTrustedEmbedCommand = (e) => {
  const parentOrigin = getEmbedParentOrigin();
  if (!parentOrigin || !e || e.source !== window.parent) return false;
  if (parentOrigin === PANEL_ORIGIN) {
    return (
      PANEL_MESSAGE_ORIGINS.indexOf(e.origin) !== -1 &&
      !!e.data && e.data.ccPanelToken === getPanelToken()
    );
  }
  return e.origin === parentOrigin;
};

/** Post a message to the allowlisted parent (no-op when not embedded/allowed). */
export const postToEmbedParent = (payload) => {
  const target = getEmbedPostTarget();
  if (!target) return false;
  try {
    window.parent.postMessage(payload, target);
    return true;
  } catch (e) {
    return false;
  }
};
